import React, { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useSession } from '@battleops/client/src/context/SessionContext';
import { useProjects } from '@battleops/client/src/context/ProjectContext';
import Icon from '../components/Icon';
import Crest, { useAvatarUri } from '../components/Crest';
import SyncPill from './SyncPill';
import DepartmentSwitcher from './DepartmentSwitcher';
import { HOME, PINNED, STATIONS, stationFor } from '../nav';
import { usePref } from '../hooks';
import s from './shell.module.css';
import { confirm } from '../components/Dialog';

// The phone drawer (DrawerContent), desk-sized. You stay pinned at the top (the crest in the brand orange, the lock in
// the corner); Today, then the work stations as an accordion — one open at a time, the page's own station opens by
// itself, a closed one previews what's inside; Settings / sign out in the footer.
// Locked = docked open. Unlocked = a slim strip (you + Settings) that slides open inside the frame while the cursor is
// on it; ‹ closes it and it stays closed until the cursor leaves.
export default function Rail({ open: expanded, locked, onLock, onClose, onNavigate }) {
  const { identity, access, signOut, sync } = useSession();
  const { projects } = useProjects();
  const { pathname } = useLocation();
  const stations = STATIONS.filter((st) => !st.foot && (!st.admin || !access))
    .map((st) => ({ ...st, items: st.items.filter((it) => !it.admin || !access) }));
  const here = stationFor(pathname)?.key || null;
  const [open, setOpen] = usePref('rail.station', here || 'plan');
  useEffect(() => { if (here) setOpen(here); }, [here, setOpen]);
  const initial = (identity?.name || identity?.email || '?').trim().charAt(0).toUpperCase();
  const avatar = useAvatarUri(identity?.avatarPath);
  const inSettings = pathname.startsWith('/settings');

  const out = () => {
    const waiting = sync.pending > 0 ? `${sync.pending} change${sync.pending === 1 ? ' is' : 's are'} still waiting to upload — they stay in this browser and upload when you sign back in as the same person.` : '';
    confirm({ title: 'Sign out of BattleOps?', message: waiting || 'Everything is uploaded.', icon: 'log-out', confirmLabel: 'Sign out', danger: !!waiting }).then((ok) => ok && signOut());
  };

  if (!expanded) {
    return (
      <nav className={s.rail} data-compact aria-label="Main">
        <div className={s.stripTop}>
          <NavLink to="/profile" className={s.stripCrest} title="Profile" aria-label="Profile"><span className={s.crestGlow}><Crest width={34} uri={avatar} initials={initial} border="var(--c-accent)" /></span></NavLink>
        </div>
        <span className="spacer" />
        <div className={s.stripFoot}>
          <NavLink to="/settings" end className={s.footBtn} title="Settings" aria-label="Settings"><Icon name="settings" size={18} /></NavLink>
        </div>
      </nav>
    );
  }

  const item = (it, nested) => (
    <NavLink key={it.to} to={it.to} end={it.end} className={s.navItem} data-nested={nested || undefined} onClick={onNavigate} aria-label={it.label}>
      <Icon name={it.icon} size={nested ? 17 : 19} /><span>{it.label}</span>
      {it.to === '/projects' && projects.length ? <span className={s.badge}>{projects.length}</span> : null}
    </NavLink>
  );

  return (
    <nav className={s.rail} aria-label="Main">
      <div className={s.drawerInner}>
        <div className={s.head}>
          <NavLink to="/profile" className={s.me} onClick={onNavigate}>
            <span className={s.crestGlow}><Crest width={50} uri={avatar} initials={initial} border="var(--c-accent)" /></span>
            <span className={s.meText}>
              <span className={s.meName}>{identity?.name || identity?.email || 'Signed out'}</span>
              {identity?.name && identity?.email ? <span className={s.meEmail}>{identity.email}</span> : null}
              <span className={s.meLink}>View profile</span>
            </span>
          </NavLink>
          <div className={s.headBtns}>
            <button className={s.lockBtn} data-on={locked || undefined} aria-pressed={locked} onClick={onLock}
              title={locked ? 'Locked open — click to unlock (then it opens when your cursor is over it)  [' : 'Lock it open  ['}
              aria-label={locked ? 'Unlock the menu' : 'Lock the menu open'}>
              <Icon name={locked ? 'lock' : 'unlock'} size={16} />
            </button>
            {!locked ? <button className={s.lockBtn} onClick={onClose} title="Close" aria-label="Close the menu"><Icon name="chevron-left" size={17} /></button> : null}
          </div>
        </div>
        <DepartmentSwitcher />
        <div className={s.meRow}><span className="num">v{__APP_VERSION__}</span><SyncPill /></div>

        <div className={s.railScroll}>
          {item(HOME, false)}
          {PINNED.map((it) => item(it, false))}
          {stations.map((st) => {
            if (st.to) {
              return (
                <NavLink key={st.key} to={st.to} className={s.stationHead} aria-label={st.label} onClick={onNavigate}>
                  <Icon name={st.icon} size={16} /><span className={s.stationText}>{st.label}</span>
                  <span className="spacer" /><Icon name="chevron-right" size={16} />
                </NavLink>
              );
            }
            const on = open === st.key;
            return (
              <div key={st.key}>
                <button className={s.stationHead} data-open={on || undefined} aria-expanded={on} onClick={() => setOpen(on ? null : st.key)}>
                  <Icon name={st.icon} size={16} /><span className={s.stationText}>{st.label}</span>
                  <span className={s.stationHint}>{on ? '' : st.items.map((i) => i.label).join(' · ')}</span>
                  <Icon name={on ? 'chevron-up' : 'chevron-down'} size={16} />
                </button>
                {on ? st.items.map((it) => item(it, true)) : null}
              </div>
            );
          })}
        </div>

        <div className={s.railFoot}>
          <NavLink to="/settings" end className={s.footLink} aria-current={inSettings ? 'page' : undefined} title="Settings" onClick={onNavigate}><Icon name="settings" size={18} /><span>Settings</span></NavLink>
          <span className="spacer" />
          <button className={s.footBtn} data-danger onClick={out} title="Sign out" aria-label="Sign out"><Icon name="log-out" size={18} /></button>
        </div>
      </div>
    </nav>
  );
}
